import React, { useMemo } from "react";

const sanitizeHtml = (dirty = "") => {
  if (!dirty) return "";

  const parser = new DOMParser();
  const doc = parser.parseFromString(dirty, "text/html");

  // remove dangerous tags
  doc.querySelectorAll("script, style, iframe, object").forEach((el) => {
    el.remove();
  });

  // remove inline event handlers (onclick, onerror, etc.)
  doc.querySelectorAll("*").forEach((el) => {
    [...el.attributes].forEach((attr) => {
      if (attr.name.startsWith("on")) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return doc.body.innerHTML;
};

/**
 * Extract plain text from HTML
 */
const htmlToText = (html = "") => {
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

const TextRenderer = ({
  html,
  height,
  className,
  style = {},
  sanitize = true,
  view = "render", // "render" | "text"
  padding="12px 14px",
}) => {
  const processedHtml = useMemo(
    () => (sanitize ? sanitizeHtml(html) : html),
    [html, sanitize]
  );

  const plainText = useMemo(() => htmlToText(processedHtml), [processedHtml]);

  return (
    <div
      className={className}
      style={{
        height,
        overflowY: height ? "auto" : "visible",
        padding: padding,
        ...style,
      }}
    >
      {view === "render" ? (
        <div
          className="ql-editor ql-renderer"
          dangerouslySetInnerHTML={{ __html: processedHtml }}
        />
      ) : (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "inherit",
            fontSize: 14,
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          {plainText}
        </pre>
      )}

      <style>{`
        .ql-renderer {
          font-family: inherit;
          line-height: 1.6;
        }

        .ql-renderer p {
          margin: 0 0 8px;
        }

        /* Quill font sizes */
        .ql-size-small { font-size: 12px; }
        .ql-size-large { font-size: 18px; }
        .ql-size-huge { font-size: 24px; }

        .ql-renderer ul,
        .ql-renderer ol {
          padding-left: 20px;
        }

        .ql-renderer a {
          color: #2563eb;
          text-decoration: underline;
        }

        .ql-renderer code {
          background: #f3f4f6;
          padding: 2px 4px;
          border-radius: 4px;
          font-size: 13px;
        }
      `}</style>
    </div>
  );
};

export default TextRenderer;
